import { css, keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { Instant } from "@js-joda/core";
import { motion } from "framer-motion";
import React from "react";
import Lottie from "react-lottie";
import Confetti from "../../../../public/assets/lotties/confetti.json";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import useDynamicWinnerSelector from "../hooks/useDynamicWinnerSelector";
import { RaffleParticipant } from "../Raffle.types";
import { default as TrophyIcon } from "/public/assets/trophy.svg?react";

type Props = {
  participants: RaffleParticipant[];
  spinPayload: {
    active: boolean;
    winnerParticipantIndex: number;
  } | null;
  showConfetti: boolean;
  animatePulse: boolean;
  raffleEndsAt: Instant;
  onWinAnimationEnd: () => void;
  onSpinIteration: () => void;
};

const lottieOptions = {
  loop: true,
  autoplay: true,
  animationData: Confetti,
  rendererSettings: {
    preserveAspectRatio: "xMidYMid slice",
  },
};

export default function RaffleDynamicWinnerSelector(props: Props) {
  const { transformY, currentParticipant } = useDynamicWinnerSelector(props);

  return (
    <DynamicWinnerSelectorWrapper>
      <ContainerCard animatePulse={props.animatePulse}>
        <motion.div
          animate={{ rotate: [0, 15, 0, -15, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <Trophy />
        </motion.div>
        <AnimationWrapper style={{ y: transformY }}>
          <ParticipantCard>
            <Avatar src={currentParticipant.avatar} />
            <WinnerDetails>
              <WinnerName>{currentParticipant.name}</WinnerName>
              <WinnerPrize>
                {currentParticipant.tokens} {translate(STRINGS.TOKENS)}
              </WinnerPrize>
            </WinnerDetails>
          </ParticipantCard>
        </AnimationWrapper>
      </ContainerCard>
      {props.showConfetti && (
        <LottieWrapper>
          <Lottie options={lottieOptions} />
        </LottieWrapper>
      )}
    </DynamicWinnerSelectorWrapper>
  );
}

const AnimationWrapper = styled(motion.div)`
  padding: 16px 0;
  flex: 1;
`;

const DynamicWinnerSelectorWrapper = styled.div`
  padding: 24px;
`;

const Trophy = styled(TrophyIcon)`
  width: 48px;
  height: 48px;
`;

const pulse = keyframes`
	0% {
		box-shadow: 0 0 0 0 rgba(142, 68, 173, 0.7);
	}
	
	70% {
		box-shadow: 0 0 0 20px rgba(142, 68, 173, 0);
	}
	
	100% {
		box-shadow: 0 0 0 0 rgba(142, 68, 173, 0);
	}
`;

const ContainerCard = styled.div<{ animatePulse: boolean }>`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 16px;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 8px;
  flex: 1;
  overflow: hidden;
  position: relative;

  ${({ animatePulse }) =>
    animatePulse &&
    css`
      animation: ${pulse} 2s ease-in-out 2;
    `}
`;

const ParticipantCard = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.3);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const WinnerDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const WinnerName = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #333;
`;

const WinnerPrize = styled.div`
  font-size: 14px;
  color: #666;
`;

const Avatar = styled.div<{ src: string | null }>`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 2px solid #fff;
  background-image: url(${(props) => props.src ?? "/assets/avatar.svg"});
  background-size: cover;
  background-position: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const LottieWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;
